import React, {useContext, useEffect, useRef, useState, useTransition} from 'react';
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {
    getAvailabilities,
    getCategories,
    getItemGroups,
    getItems,
    getMenus,
    getPromotions
} from "../../../services/Menu/reducer";
import {RootState} from "../../../../reducers";
import PromotionHelper from "../../../services/Menu/services/Promotion/PromotionHelper";
import {useNavigate} from "react-router-dom";
import Languages from "../../../components/Common/Languages";
import {getStoreConfig} from "../../../services/Store/reducer";
import Sticky from "react-stickynode";
import {getSession} from "../../../services/Session/reducer";
import MenuSearchFilterComponent from "./Filter/MenuSearchFilterComponent";
import AppConfig from "../../../services/AppConfig";
import {usePrefix} from "../../../services/Prefix";
import {getCart} from "../../../services/Cart/reducer";
import {useMainStickyNav} from "../../Contexts/MainNavStickyContext";
import useTemporarilyClosedInfo from "../../../hooks/useTemporarilyClosedInfo";
import StoreUtils from "../../../services/Store/StoreUtils";
import MenuCategorySection from "./Category/MenuCategorySection";
import ResponsiveComponent from "../../../components/Common/ResponsiveComponent";
import {MenuSearchContext} from "../../../services/Menu/services/Search/MenuSearchContext";
import {ReactComponent as SearchIco} from "../../../../images/icons/search.svg";
import EmptyList from "../../../components/Common/EmptyList";
import MenuCategoriesNavbar from "./Category/MenuCategoriesNavbar";
import AvailableHelper from "../../../services/Menu/services/Availability/AvailableHelper";
import {MenuProductViewType, ParsedCategory} from "../../../services/Api/types";
import CategoryHelper from "../../../services/Menu/services/Category/CategoryHelper";
import MenuTabs from "./Menu/MenuTabs";
import AgeRestrictedContext from "../../../services/Common/AgeRestrictedContext";

interface OwnProps {
    menuId: number | undefined;
}

type Props = ReturnType<typeof mapStateToProps> & DispatchProp & OwnProps;

const MenuComponent = ({
                           cart,
                           config,
                           items,
                           session,
                           itemGroups,
                           categories,
                           promotions,
                           availabilities,
                           menus,
                           menuId
                       }: Props) => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const prefix = usePrefix();
    const {handleShowTemporarilyClosedInfo, drawTemporarilyClosedInfo} = useTemporarilyClosedInfo();
    const {setMainNavSticky} = useMainStickyNav();
    const {showSearchBar, search} = useContext(MenuSearchContext);
    const [availableCategoryIds, setAvailableCategoryIds] = useState<number[]>([]);
    const productsViewType = StoreUtils.getMenuProductsViewType(config);
    const notStickyHeightRef = useRef<number | null>(null);
    const isMultilevelMenu = StoreUtils.isMultilevelMenu(config);
    const [currentCategories, setCurrentCategories] = useState<ParsedCategory[]>([]);
    const [_, startTransition] = useTransition();
    const isAgeConfirmed = AgeRestrictedContext.isAccepted(StoreUtils.isAgeRestrictionDisabled(config));

    useEffect(() => {
        new IntersectionObserver(
            ([entry]) => {
                const isSticky = !entry.isIntersecting;
                setMainNavSticky(!isSticky);
            },
            {
                threshold: 0,
                rootMargin: '0px 0px 0px 0px',
            }
        );
    }, []);

    useEffect(() => {
        const header = document.querySelector('.sticky-outer-wrapper') as HTMLElement;
        if (!header) return;

        if (notStickyHeightRef.current) {
            header.style.setProperty('height', `${notStickyHeightRef.current}px`, 'important');
        }

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.attributeName === 'style' && notStickyHeightRef.current) {
                    const currentHeight = header.style.height;
                    const desiredHeight = `${notStickyHeightRef.current}px`;

                    if (currentHeight !== desiredHeight) {
                        header.style.setProperty('height', desiredHeight, 'important');
                    }
                }
            }
        });

        observer.observe(header, {
            attributes: true,
            attributeFilter: ['style'],
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        startTransition(() => {
            const parsed = getParsedCategories();
            setCurrentCategories(parsed);
        });
    }, [menuId, session.order_type, categories, items, availabilities, promotions, cart, search, isAgeConfirmed]);

    const addItem = (id: number, category: Record<string, any>) => {
        if (StoreUtils.isPaused(config)) {
            handleShowTemporarilyClosedInfo();
            return;
        }

        let back = true;

        if (StoreUtils.isHomepageMenu(config)) {
            back = false;
        }

        const link = isMultilevelMenu && menuId ? `/menu/${menuId}/add/${id}` : `/menu/add/${id}`;
        navigate(AppConfig.getLinkWithPrefix(link, prefix), {
            state: {
                back,
                categoryIds: category.originalIds
            }
        });
    }

    const getFilteredItems = (items: any[]) => {
        if (!search) return items;

        return items.filter((item: any) => item.translation.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
    }

    const getParsedCategories = () => {
        const availablePromotions = PromotionHelper.getActivePromotionsForCategory(promotions, cart.total);
        const filteredItems = getFilteredItems(items);

        const parsedCategories: any[] = CategoryHelper.getParsedCategories(
            categories,
            filteredItems,
            availabilities,
            availablePromotions,
            session,
            cart,
            itemGroups,
            menus,
            AgeRestrictedContext.isAccepted(StoreUtils.isAgeRestrictionDisabled(config)),
            isMultilevelMenu,
            menuId,
            StoreUtils.isOrderingReadonly(config)
        ).filter((category: any) => {
            const menusWithCategory = isMultilevelMenu ? (menuId ? [menus.find((menu: Record<string, any>) => menu.id === menuId)] : []) : CategoryHelper.getMenusForCategoryByIds(category, menus);
            const menuAvailabilities = AvailableHelper.getAvailabilities(availabilities, menusWithCategory.map((menu: any) => menu.availability_id));
            return menuAvailabilities.length > 0;
        }).filter(Boolean);

        return isMultilevelMenu ? CategoryHelper.getSortedCategoriesByPosition(parsedCategories) : CategoryHelper.getSortedParsedCategoriesByMenu(parsedCategories, menus);
    }

    const handleStickyStateChange = ({status}: Sticky.Status) => {
        const header = document.querySelector('.sticky-outer-wrapper') as HTMLElement;

        if (status === 2) {
            setMainNavSticky(false);

            if (header) {
                if (!notStickyHeightRef.current) {
                    notStickyHeightRef.current = header.clientHeight;
                }

                header.style.setProperty('height', `${notStickyHeightRef.current}px`, 'important');
            }
        } else {
            setMainNavSticky(true);
        }
    };

    return (
        <div className="menu-container-with-gap">
            <ResponsiveComponent small={
                <>
                    {showSearchBar && (
                        <div className="mt-2">
                            <MenuSearchFilterComponent hideOnClear/>
                        </div>
                    )}
                    {
                        !showSearchBar && (
                            <div className="menu-container-with-gap">
                                <Languages size={"xl"}/>
                                <MenuTabs menuId={menuId}/>
                                {currentCategories.length > 1 && (
                                    <div>
                                        <Sticky top={0} bottomBoundary="#footer" innerZ={AppConfig.isWidget() ? 1019 : 1021}
                                                activeClass="is-sticky"
                                                onStateChange={handleStickyStateChange}>
                                            <header
                                                className="parent-top-nav sticky-fixed1 sticky-fixed-categories"
                                                id="nav-scrollspy">
                                                <MenuCategoriesNavbar promotions={promotions}
                                                                      categories={currentCategories}/>
                                            </header>
                                        </Sticky>
                                    </div>
                                )}
                            </div>
                        )
                    }
                </>
            } large={
                <div className="menu-container-with-gap">
                    <MenuSearchFilterComponent/>
                    <Languages size={"xl"}/>
                    <MenuTabs menuId={menuId}/>
                    {currentCategories.length > 1 && (
                        <Sticky top={0} bottomBoundary="#footer" innerZ={AppConfig.isWidget() ? 1019 : 1021}
                                activeClass="is-sticky"
                                onStateChange={handleStickyStateChange}>
                            <header
                                className="parent-top-nav sticky-fixed1 sticky-fixed-categories"
                                id="nav-scrollspy">
                                <MenuCategoriesNavbar promotions={promotions}
                                                      categories={currentCategories}/>
                            </header>
                        </Sticky>
                    )}
                </div>
            } breakpoint={768}
            />
            {
                currentCategories.length === 0 && search ? (
                        <div>
                            <EmptyList icon={<SearchIco/>} title={t("constraints.internal.no_items_found")}/>
                        </div>
                    ) :
                    <div
                        className={`d-flex flex-column ${productsViewType === MenuProductViewType.HORIZONTAL ? "gap-4 mb-5" : ""}`}>
                        {currentCategories.map((category) => (
                            <MenuCategorySection
                                key={`${category.id}-${session.order_type}-${availableCategoryIds.length === 0}`}
                                productsViewType={productsViewType}
                                category={category}
                                forceExpandItems={availableCategoryIds.length === 0}
                                promotions={promotions} setAvailableCategoryIds={setAvailableCategoryIds}
                                addItem={(id: number) => addItem(id, category)}
                                menuId={menuId}
                            />
                        ))}
                    </div>
            }
            {drawTemporarilyClosedInfo()}
        </div>
    )
}

const mapStateToProps = (state: RootState) => ({
    availabilities: getAvailabilities(state.menu),
    promotions: getPromotions(state.menu),
    categories: getCategories(state.menu),
    items: getItems(state.menu),
    itemGroups: getItemGroups(state.menu),
    session: getSession(state.session),
    config: getStoreConfig(state.config),
    cart: getCart(state.cart),
    menus: getMenus(state.menu),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(MenuComponent));